/**
 * Google Maps renderer for the LA / NY store-detail pages.
 *
 * Geocodes each `.js-google-map[data-map-address]` and drops a
 * RichMarker (HTML overlay) using the next sibling `.js-marker` as
 * the marker template. Gated by `flags__gmaps_enable` so the
 * geocoder isn't billed on environments without the API key wired up.
 *
 * Migrated from theme/js/modules/maps.js (IIFE → ESM, Phase 11).
 * Phase 13.3: replaced the GMaps wrapper + js-rich-marker (Bower) with
 * native `window.google.maps.*` calls plus an inline OverlayView, so
 * Bower can go entirely. Self-binds at module evaluation; the
 * ep:ready / ep:change listeners only attach if the feature flag is
 * set, mirroring the IIFE init() guard.
 *
 * Also fixes a latent bug: the original IIFE leaked `mapStyles` as
 * an implicit global (assigned without `var`), which crashed in
 * strict mode. ESM is strict by default, so the fix is mandatory.
 *
 * Source path: theme/js/modules/maps.esm.js
 * Built name : ./maps.js (sibling imports must use the built name)
 *
 * AI: Entirely written by AI
 * Date: 2026-05-05
 * Model: Claude Opus 4.7
 * Updated: 2026-06-28 (Claude Opus 4.8) - Wait for the Maps SDK to finish
 *   loading before rendering, so maps appear when the stores page is reached
 *   via a Turbo navigation (the `<script defer>` SDK loader in the swapped-in
 *   body is still loading async when ep:change fires).
 * Updated: 2026-09-28 (Claude Fable 5.1) - Load the SDK from here, on demand:
 *   the footer used to ship the 100 KB script on every page for the two store
 *   pages that use it (a 300–400 ms long task on mobile). The map node carries
 *   the SDK URL (`data-sdk-url`, store-details.liquid); the first page with a
 *   map injects it once into <head>, which Turbo keeps across navigations, and
 *   renders on the script's onload — which retired the 100 ms poll.
 *
 * [Maps]
 */

import { log } from './log.js';
import { appGet } from './app-get.js';

const MAP_SELECTOR = '.js-google-map';

const mapStyles = [
  { elementType: 'geometry', stylers: [{ color: '#f0f0f0' }] },
  { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#f5f5f5' }] },
  {
    featureType: 'administrative.land_parcel',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#bdbdbd' }],
  },
  { featureType: 'poi', elementType: 'geometry', stylers: [{ color: '#eeeeee' }] },
  { featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
  { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#e5e5e5' }] },
  { featureType: 'poi.park', elementType: 'geometry.fill', stylers: [{ color: '#91ba96' }] },
  { featureType: 'poi.park', elementType: 'labels.icon', stylers: [{ visibility: 'on' }] },
  { featureType: 'poi.park', elementType: 'labels.text.fill', stylers: [{ color: '#337544' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
  {
    featureType: 'road.arterial',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#757575' }],
  },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#dadada' }] },
  { featureType: 'road.highway', elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
  { featureType: 'road.local', elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] },
  { featureType: 'transit.line', elementType: 'geometry', stylers: [{ color: '#e5e5e5' }] },
  { featureType: 'transit.station', elementType: 'geometry', stylers: [{ color: '#eeeeee' }] },
  {
    featureType: 'transit.station.rail',
    stylers: [{ color: '#5b6795' }, { visibility: 'on' }, { weight: 1 }],
  },
  {
    featureType: 'transit.station.rail',
    elementType: 'labels.text.stroke',
    stylers: [{ color: '#ffffff' }, { visibility: 'simplified' }, { weight: 0.5 }],
  },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#a8cdd6' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] },
];

// The module evaluates once per document and <head> persists across Turbo
// navigations, so one boolean is enough to inject the SDK exactly once.
let sdkRequested = false;

function sdkReady() {
  return !!(window.google && window.google.maps);
}

// Inject the SDK <script> into <head> (Turbo merges <head> rather than
// swapping it, so the script — and window.google — survive navigations) and
// render when it lands. renderMaps re-queries the DOM, so a Turbo navigation
// made while the SDK is still loading is rendered by this same onload.
function ensureSdk(src) {
  if (sdkRequested) return;
  if (!src) {
    log('[Maps] map node has no data-sdk-url - SDK not loaded');
    return;
  }
  sdkRequested = true;
  const script = document.createElement('script');
  script.src = src;
  script.onload = renderMaps;
  script.onerror = () => log('[Maps] SDK failed to load - maps will not be rendered');
  document.head.appendChild(script);
  log('[Maps] SDK requested');
}

function setupMaps() {
  const firstMap = document.querySelector(MAP_SELECTOR);
  if (!firstMap) return;
  if (sdkReady()) {
    renderMaps();
    return;
  }
  ensureSdk(firstMap.dataset.sdkUrl);
}

function renderMaps() {
  const geocoder = new window.google.maps.Geocoder();

  // Re-query at render time — on a Turbo navigation the body that held the
  // initial NodeList may have been swapped out by the time the SDK is ready.
  document.querySelectorAll(MAP_SELECTOR).forEach(mapEl => {
    // Skip maps already rendered (e.g. a stray re-fire of ep:change) so the
    // geocoder isn't re-billed and markers aren't duplicated.
    if (mapEl.classList.contains('rendered')) return;
    const address = mapEl.getAttribute('data-map-address');
    geocoder.geocode({ address }, (results, status) => {
      if (status !== 'OK' || !results || !results[0]) return;
      const latLng = results[0].geometry.location;
      const map = new window.google.maps.Map(mapEl, {
        center: latLng,
        zoom: 15,
        styles: mapStyles,
        disableDefaultUI: true,
      });

      mapEl.classList.add('rendered');

      // The IIFE used $map.next('.js-marker:first') — the first
      // .js-marker that comes after the map in the DOM. Native walk.
      let sibling = mapEl.nextElementSibling;
      let markerEl = null;
      while (sibling) {
        if (sibling.classList && sibling.classList.contains('js-marker')) {
          markerEl = sibling;
          break;
        }
        sibling = sibling.nextElementSibling;
      }
      if (!markerEl) return;

      // Phase 13.3: replaced RichMarker (Bower) with a native Google
      // Maps OverlayView. Same contract — render `markerEl.outerHTML`
      // anchored at `latLng`, non-draggable, flat (no shadow).
      const Overlay = function (position, html) {
        this._position = position;
        this._html = html;
      };
      Overlay.prototype = new window.google.maps.OverlayView();
      Overlay.prototype.onAdd = function () {
        const div = document.createElement('div');
        div.style.position = 'absolute';
        div.innerHTML = this._html;
        this._div = div;
        this.getPanes().overlayMouseTarget.appendChild(div);
      };
      Overlay.prototype.draw = function () {
        if (!this._div) return;
        const projection = this.getProjection();
        if (!projection) return;
        const point = projection.fromLatLngToDivPixel(this._position);
        if (!point) return;
        // Center the rendered HTML over the lat/lng anchor.
        this._div.style.left = point.x - this._div.offsetWidth / 2 + 'px';
        this._div.style.top = point.y - this._div.offsetHeight / 2 + 'px';
      };
      Overlay.prototype.onRemove = function () {
        if (this._div && this._div.parentNode) {
          this._div.parentNode.removeChild(this._div);
          this._div = null;
        }
      };
      const overlay = new Overlay(latLng, markerEl.outerHTML);
      overlay.setMap(map);
    });
  });
}

function isEnabled() {
  return appGet('flags.flags__gmaps_enable') === true;
}

function startWhenReady() {
  if (!isEnabled()) {
    log('-- Google Maps feature flag disabled - maps will not be rendered');
    return;
  }
  setupMaps();
}

document.addEventListener('ep:ready', startWhenReady);
document.addEventListener('ep:change', startWhenReady);

log('[Maps] registered');
